import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  Users,
  Loader2,
  MoreVertical,
  Mail,
  Phone,
  Globe,
  Building2,
  Pencil,
  Trash2,
  MessageSquare,
  ArrowRightLeft,
  User,
  Key,
  LogIn
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Clients = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders, impersonateClient } = useAuth();
  const [clients, setClients] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [communications, setCommunications] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCommOpen, setIsCommOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company: '',
    website: '',
    notes: '',
    organization_id: ''
  });
  const [commData, setCommData] = useState({
    subject: '',
    message: '',
    communication_type: 'note'
  });
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferTenantId, setTransferTenantId] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const handleViewAsClient = async (client) => {
    try {
      const userData = await impersonateClient(client.id);
      toast.success(`Viewing portal as ${userData.name || client.name}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter client portal');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const clientsRes = await axios.get(`${API_URL}/clients`, getAuthHeaders());
      setClients(clientsRes.data);

      if (isSuperAdmin) {
        const tenantsRes = await axios.get(`${API_URL}/tenants`, getAuthHeaders());
        setOrganizations(tenantsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunications = async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/communications?client_id=${clientId}`, getAuthHeaders());
      setCommunications(prev => ({ ...prev, [clientId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch communications:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate email and password for client login
    if (!formData.email || !formData.password) {
      toast.error('Email and password are required for client login');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      const data = {
        ...formData,
        organization_id: isSuperAdmin ? formData.organization_id : user.organization_id
      };
      
      await axios.post(`${API_URL}/clients`, data, getAuthHeaders());
      toast.success('Client created with login account');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/clients/${selectedClient.id}`, formData, getAuthHeaders());
      toast.success('Client updated successfully');
      setIsEditOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await axios.delete(`${API_URL}/clients/${clientId}`, getAuthHeaders());
      toast.success('Client deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleSendCredentials = async (clientId, clientName, clientEmail) => {
    if (!window.confirm(`Send login credentials to ${clientName} (${clientEmail})?\n\nThis will reset their password and send a new one via email.`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/clients/${clientId}/send-credentials`, {}, getAuthHeaders());
      toast.success(response.data.message || 'Credentials sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credentials');
    }
  };

  const handleAddCommunication = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/communications`, {
        ...commData,
        client_id: selectedClient.id
      }, getAuthHeaders());
      toast.success('Communication added');
      setCommData({ subject: '', message: '', communication_type: 'note' });
      fetchCommunications(selectedClient.id);
    } catch (error) {
      toast.error('Failed to add communication');
    }
  };

  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      website: client.website || '',
      notes: client.notes || '',
      organization_id: client.organization_id
    });
    setIsEditOpen(true);
  };

  const openCommModal = (client) => {
    setSelectedClient(client);
    fetchCommunications(client.id);
    setIsCommOpen(true);
  };

  const openTransferModal = (client) => {
    setSelectedClient(client);
    setTransferTenantId('');
    setIsTransferOpen(true);
  };

  const handleTransferClient = async () => {
    if (!transferTenantId) {
      toast.error('Please select a target tenant');
      return;
    }

    if (transferTenantId === selectedClient?.organization_id) {
      toast.error('Client already belongs to this tenant');
      return;
    }

    setTransferLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/clients/${selectedClient.id}/transfer`,
        { new_tenant_id: transferTenantId },
        getAuthHeaders()
      );
      
      const summary = response.data.summary;
      toast.success(
        `Client transferred! Projects: ${summary.projects_transferred}, Invoices: ${summary.invoices_transferred}, Meetings: ${summary.meetings_transferred}`
      );
      setIsTransferOpen(false);
      setSelectedClient(null);
      setTransferTenantId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer client');
    } finally {
      setTransferLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      company: '',
      website: '',
      notes: '',
      organization_id: ''
    });
    setSelectedClient(null);
  };

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Clients
          </h1>
          <p className="text-slate-500 mt-1">Manage your client relationships</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-client-btn">
              <Plus size={18} className="mr-2" />
              New Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add New Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., John Smith"
                  required
                  data-testid="client-name-input"
                />
              </div>

              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label>Tenant *</Label>
                  <Select
                    value={formData.organization_id}
                    onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                  >
                    <SelectTrigger data-testid="client-tenant-select">
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="client@example.com"
                    data-testid="client-email-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password <span className="text-red-500">*</span></Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Login password"
                    data-testid="client-password-input"
                    required
                  />
                  <p className="text-xs text-slate-500">Client will use this to login to their portal</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    data-testid="client-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Name</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Client/Company name"
                    data-testid="client-company-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                    data-testid="client-website-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this client..."
                  rows={3}
                  data-testid="client-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-client-btn">
                  Add Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search clients by name, company, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-clients-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No clients found</h3>
            <p className="text-slate-500 mt-1">Add your first client to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <Card 
              key={client.id} 
              className="border-slate-200 shadow-sm card-hover cursor-pointer"
              onClick={() => navigate(`/clients/${client.id}`)}
              data-testid={`client-card-${client.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg font-semibold text-slate-900 truncate">
                      {client.company || client.name}
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                      <User size={14} />
                      {client.name}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`client-menu-${client.id}`}>
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openCommModal(client)}>
                        <MessageSquare size={14} className="mr-2" />
                        Communications
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditModal(client)}>
                        <Pencil size={14} className="mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {client.email && (
                        <DropdownMenuItem 
                          onClick={() => handleSendCredentials(client.id, client.name, client.email)}
                          data-testid={`send-credentials-${client.id}`}
                        >
                          <Key size={14} className="mr-2" />
                          Send Credentials
                        </DropdownMenuItem>
                      )}
                      {isSuperAdmin && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openTransferModal(client); }}>
                          <ArrowRightLeft size={14} className="mr-2" />
                          Transfer to Tenant
                        </DropdownMenuItem>
                      )}
                      {isSuperAdmin && (
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); handleViewAsClient(client); }}
                          className="text-blue-600 focus:text-blue-600"
                        >
                          <LogIn size={14} className="mr-2" />
                          View as Client
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => handleDelete(client.id)}
                        className="text-red-600"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {client.email && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Mail size={14} className="text-slate-400" />
                      <a href={`mailto:${client.email}`} className="hover:text-blue-600 truncate">
                        {client.email}
                      </a>
                    </p>
                  )}
                  {client.phone && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Phone size={14} className="text-slate-400" />
                      <a href={`tel:${client.phone}`} className="hover:text-blue-600">
                        {client.phone}
                      </a>
                    </p>
                  )}
                  {client.website && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Globe size={14} className="text-slate-400" />
                      <a href={client.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 truncate">
                        {client.website.replace(/^https?:\/\//, '')}
                      </a>
                    </p>
                  )}
                </div>

                {client.notes && (
                  <p className="text-sm text-slate-500 mt-3 pt-3 border-t border-slate-100 line-clamp-2">
                    {client.notes}
                  </p>
                )}

                {/* Tenant Name - visible for Super Admin */}
                {isSuperAdmin && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-400 flex items-center gap-1.5">
                      <Building2 size={12} className="text-slate-300" />
                      <span className="font-medium">
                        {organizations.find(o => o.id === client.organization_id)?.company_name || 
                         organizations.find(o => o.id === client.organization_id)?.name || 
                         'Unknown Tenant'}
                      </span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Name</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="update-client-btn">
                Update Client
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Communications Modal */}
      <Dialog open={isCommOpen} onOpenChange={setIsCommOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Communications - {selectedClient?.name}
            </DialogTitle>
          </DialogHeader>
          
          {/* Add new communication */}
          <form onSubmit={handleAddCommunication} className="space-y-4 border-b border-slate-200 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={commData.subject}
                  onChange={(e) => setCommData({ ...commData, subject: e.target.value })}
                  placeholder="Communication subject"
                  required
                  data-testid="comm-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={commData.communication_type}
                  onValueChange={(value) => setCommData({ ...commData, communication_type: value })}
                >
                  <SelectTrigger data-testid="comm-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={commData.message}
                onChange={(e) => setCommData({ ...commData, message: e.target.value })}
                placeholder="Enter communication details..."
                rows={3}
                required
                data-testid="comm-message-input"
              />
            </div>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="add-comm-btn">
              Add Communication
            </Button>
          </form>

          {/* Communications history */}
          <div className="mt-4 space-y-3">
            <h4 className="font-medium text-slate-700">History</h4>
            {communications[selectedClient?.id]?.length > 0 ? (
              <div className="space-y-3">
                {communications[selectedClient?.id].map((comm) => (
                  <div key={comm.id} className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-900">{comm.subject}</span>
                      <span className="text-xs text-slate-400 capitalize px-2 py-1 bg-slate-200 rounded-full">
                        {comm.communication_type}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{comm.message}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {format(new Date(comm.created_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">No communications yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Client Modal - Super Admin Only */}
      {isSuperAdmin && (
        <Dialog open={isTransferOpen} onOpenChange={setIsTransferOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Transfer Client to Different Tenant
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> This will transfer <strong>{selectedClient?.name}</strong> and ALL associated data to the new tenant:
                </p>
                <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
                  <li>All projects</li>
                  <li>All invoices</li>
                  <li>All meetings</li>
                  <li>All communications/notes</li>
                  <li>All tasks</li>
                  <li>All media/storage files</li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label>Current Tenant</Label>
                <Input
                  value={organizations.find(o => o.id === selectedClient?.organization_id)?.company_name || 
                         organizations.find(o => o.id === selectedClient?.organization_id)?.name || 
                         'Unknown'}
                  disabled
                  className="bg-slate-100"
                />
              </div>

              <div className="space-y-2">
                <Label>Transfer To *</Label>
                <Select
                  value={transferTenantId}
                  onValueChange={setTransferTenantId}
                >
                  <SelectTrigger data-testid="transfer-tenant-select">
                    <SelectValue placeholder="Select target tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations
                      .filter(org => org.id !== selectedClient?.organization_id)
                      .map(org => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.company_name || org.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsTransferOpen(false)}
                  disabled={transferLoading}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleTransferClient}
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={transferLoading || !transferTenantId}
                  data-testid="confirm-transfer-btn"
                >
                  {transferLoading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Transferring...
                    </>
                  ) : (
                    <>
                      <ArrowRightLeft size={16} className="mr-2" />
                      Transfer Client
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Clients;
